"use client";
import {
  ChoicePills,
  GameDemo,
} from "@/components/marketing";
import { Button } from "@/components/ui/button";
import type { ScratchJourney } from "../../data";
import { ScratchCard } from "./components";
import { useScratchCard } from "./hooks";

interface ScratchCardDemoProps {
  ariaLabel: string;
  journeyPickerLabel: string;
  stepsLabel: string;
  journeys: ScratchJourney[];
  card: {
    wonLabel: string;
    hint: string;
    revealLabel: string;
    newCardLabel: string;
  };
  screen: {
    shop: string;
    game: string;
    waitTitle: string;
    waitText: string;
    wonLabel: string;
  };
}

// The preview's scratch_card demo: choose a journey (redeem on this visit or
// the next), scratch the card, then redeem the offer on the customer's phone.
const ScratchCardDemo = ({
  ariaLabel,
  journeyPickerLabel,
  stepsLabel,
  journeys,
  card,
  screen,
}: ScratchCardDemoProps) => {
  const {
    journey,
    prize,
    phase,
    isOpen,
    code,
    cardId,
    selectJourney,
    newCard,
    reveal,
    redeem,
    redeemButtonRef,
    newCardButtonRef,
  } = useScratchCard({ journeys });

  return (
    <GameDemo
      ariaLabel={ariaLabel}
      controls={
        <ChoicePills
          ariaLabel={journeyPickerLabel}
          options={journeys.map((item) => ({ key: item.key, label: item.tabLabel }))}
          value={journey.key}
          onChange={selectJourney}
          isCentered
        />
      }
      steps={{
        labels: journey.steps,
        ariaLabel: stepsLabel,
        activeIndex: phase === "card" ? 1 : 2,
        isComplete: phase === "done",
      }}
      screen={{
        shop: screen.shop,
        game: screen.game,
        state: phase === "card" ? "wait" : phase,
        wait: { title: screen.waitTitle, text: screen.waitText },
        offer: {
          label: screen.wonLabel,
          title: prize,
          text: journey.terms,
          action: (
            <Button
              ref={redeemButtonRef}
              type="button"
              variant="brand"
              size="pill"
              onClick={redeem}
            >
              {journey.actionLabel}
            </Button>
          ),
        },
        done: {
          title: journey.doneTitle,
          text: journey.doneText.replace("{code}", code),
        },
      }}
    >
      <p className="max-w-[36ch] text-center font-bold">{journey.context}</p>
      <ScratchCard
        cardId={cardId}
        isOpen={isOpen}
        onReveal={reveal}
        wonLabel={card.wonLabel}
        prize={prize}
        shop={screen.shop}
        hint={card.hint}
      />
      <div className="flex flex-wrap justify-center gap-2.5">
        <Button
          type="button"
          variant="brandOutline"
          size="pillSm"
          disabled={isOpen}
          onClick={reveal}
        >
          {card.revealLabel}
        </Button>
        <Button
          ref={newCardButtonRef}
          type="button"
          variant="brand"
          size="pillSm"
          onClick={newCard}
        >
          {card.newCardLabel}
        </Button>
      </div>
    </GameDemo>
  );
};

export default ScratchCardDemo;
